import { Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Button } from '@/ui/primitives/button';
import { type GrupoComando } from '@/ui/primitives/command';
import { Sheet } from '@/ui/primitives/sheet';
import { ProvedorDicas } from '@/ui/primitives/tooltip';

import { AlternarTema } from './alternar-tema';
import { BarraLateral } from './barra-lateral';
import { GatilhoPaleta, PaletaComandos } from './paleta-comandos';
import { type GrupoNavegacao } from './tipos';
import { atalhoMenu, ehDesktop, useMenuRecolhido } from './use-menu-recolhido';
import { usePaletaComandos } from './use-paleta-comandos';

interface CascaAppProps {
  nomeProduto: string;
  navegacao: readonly GrupoNavegacao[];
  /** Seletor de empresas (o app liga aos dados). */
  escopo: ReactNode;
  usuario: ReactNode;
  children: ReactNode;
}

/** Estrutura de toda tela autenticada: menu lateral, barra superior com escopo, conteúdo. */
export function CascaApp({ nomeProduto, navegacao, escopo, usuario, children }: CascaAppProps) {
  const [recolhida, alternar] = useMenuRecolhido();
  const [menuMovel, setMenuMovel] = useState(false);
  const [paleta, setPaleta] = usePaletaComandos();
  // A ação só faz sentido onde o menu fixo aparece; a paleta re-renderiza ao abrir.
  const acoes = ehDesktop() ? acoesDoMenu(recolhida, alternar) : [];

  return (
    <ProvedorDicas>
      <div className="flex h-dvh overflow-hidden bg-fundo">
        <div className="hidden lg:block">
          <BarraLateral
            nomeProduto={nomeProduto}
            grupos={navegacao}
            recolhida={recolhida}
            aoAlternar={alternar}
          />
        </div>
        <Sheet
          aberto={menuMovel}
          aoMudar={setMenuMovel}
          titulo={nomeProduto}
          lado="esquerda"
          className="w-barra lg:hidden"
        >
          <div
            onClick={() => {
              setMenuMovel(false);
            }}
          >
            <BarraLateral nomeProduto={nomeProduto} grupos={navegacao} recolhida={false} />
          </div>
        </Sheet>
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-3 border-b border-borda bg-superficie/80 px-4 backdrop-blur-sm lg:px-6">
            <Button
              variante="fantasma"
              tamanho="icone"
              className="lg:hidden"
              aria-label="Abrir menu"
              onClick={() => {
                setMenuMovel(true);
              }}
            >
              <Menu aria-hidden />
            </Button>
            {escopo}
            <div className="ml-auto flex items-center gap-2">
              <GatilhoPaleta
                aoAbrir={() => {
                  setPaleta(true);
                }}
              />
              <AlternarTema />
              {usuario}
            </div>
          </header>
          <main id="conteudo" className="min-h-0 flex-1 overflow-y-auto px-4 py-6 lg:px-8">
            {children}
          </main>
        </div>
      </div>
      <PaletaComandos aberta={paleta} aoMudar={setPaleta} navegacao={navegacao} acoes={acoes} />
    </ProvedorDicas>
  );
}

function acoesDoMenu(recolhida: boolean, alternar: () => void): GrupoComando[] {
  return [
    {
      titulo: 'Ações',
      itens: [
        {
          id: 'alternar-menu-lateral',
          rotulo: recolhida ? 'Expandir menu' : 'Recolher menu',
          icone: recolhida ? <PanelLeftOpen aria-hidden /> : <PanelLeftClose aria-hidden />,
          atalho: atalhoMenu(),
          aoSelecionar: alternar,
        },
      ],
    },
  ];
}
